import React, { Component } from 'react';
import { Table, Input, Button, Drawer, Tag, Spin, Avatar } from 'antd';
import { FacebookOutlined, MessageOutlined, WhatsAppOutlined, GlobalOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import moment from 'moment';
import { AccountMetaData } from '../../config/settings';
import CustomerCreateModal from './CustomerCreateModal';
import CustomerImportModal from './CustomerImportModal';

const { Search } = Input;

const getStructuredCallSummaryTags = (structuredData = {}) => {
  const data = structuredData && typeof structuredData === 'object' ? structuredData : {};
  const customer = data.customer && typeof data.customer === 'object' ? data.customer : {};
  const tags = [];
  const pushTag = (label, value, color) => {
    if (!value) return;
    const text = String(value).trim();
    if (!text) return;
    tags.push({ label, value: text, color });
  };

  (Array.isArray(data.interests) ? data.interests : []).forEach((item) => pushTag('Quan tâm', item, 'purple'));
  pushTag('Ngân sách', data.budget, 'gold');
  (Array.isArray(data.objections) ? data.objections : []).forEach((item) => pushTag('Phản đối', item, 'volcano'));
  pushTag('Thái độ', data.sentiment, 'blue');
  pushTag('Họ tên', customer.name, 'magenta');
  pushTag('SĐT', customer.phone, 'cyan');
  pushTag('Email', customer.email, 'cyan');
  pushTag('Địa chỉ', customer.address, 'geekblue');
  pushTag('Ngày hẹn', data.appointment_date, 'green');
  pushTag('Công ty', data.branch_location, 'blue');

  return {
    tags,
    notes: data.notes ? String(data.notes).trim() : ''
  };
};

export default class CustomerListView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      data: [],
      total: 0,
      page: 1,
      limit: 20,
      search: '',
      drawerVisible: false,
      selectedCustomer: null,
      loadingMemory: false,
      memoryData: null,
      loadingCallSummaries: false,
      callSummaries: [],
      createModalVisible: false,
      importModalVisible: false
    };
  }

  componentDidMount() {
    this.fetchCustomers();
  }

  fetchCustomers = async (page = 1, search = this.state.search) => {
    this.setState({ loading: true });
    try {
      const res = await this.props.fetchData({
        url: `api/v1/customer/list?page=${page}&limit=${this.state.limit}&search=${encodeURIComponent(search || '')}`,
        method: 'GET'
      });
      if (res.code === 200) {
        this.setState({
          data: res.data,
          total: res.total,
          page: res.page,
          search: search,
          loading: false
        });
      } else {
        this.setState({ loading: false });
        this.props.message({ type: 'error', content: res.message || 'Lỗi tải danh sách khách hàng' });
      }
    } catch (err) {
      this.setState({ loading: false });
      this.props.message({ type: 'error', content: 'Có lỗi xảy ra' });
    }
  }

  handleTableChange = (pagination) => {
    this.fetchCustomers(pagination.current);
  }

  onSearch = (val) => {
    this.fetchCustomers(1, val);
  }

  handleCreateSuccess = () => {
    this.setState({ createModalVisible: false });
    this.fetchCustomers(1, this.state.search);
  }

  handleImportSuccess = () => {
    this.fetchCustomers(1, this.state.search);
  }

  openDrawer = async (record) => {
    this.setState({
      drawerVisible: true,
      selectedCustomer: record,
      loadingMemory: true,
      memoryData: null,
      loadingCallSummaries: true,
      callSummaries: []
    });
    
    // Fetch memory
    try {
      const companyId = AccountMetaData && AccountMetaData.getCompanyId ? AccountMetaData.getCompanyId() : '';
      const res = await this.props.fetchData({
        url: `retrieval/memory?contactId=${record.contact_id}&companyId=${companyId}`,
        method: 'GET'
      });
      if (res.code === 200) {
        this.setState({ memoryData: res.data, loadingMemory: false });
      } else {
        this.setState({ loadingMemory: false });
      }
    } catch (err) {
      this.setState({ loadingMemory: false });
    }

    try {
      const res = await this.props.fetchData({
        url: `api/v1/contact/${record.contact_id}/call-summaries?limit=10`,
        method: 'GET'
      });
      this.setState({
        callSummaries: res && res.code === 200 && Array.isArray(res.data) ? res.data : [],
        loadingCallSummaries: false
      });
    } catch (err) {
      this.setState({ loadingCallSummaries: false, callSummaries: [] });
    }
  }

  closeDrawer = () => {
    this.setState({ drawerVisible: false, selectedCustomer: null });
  }

  renderDrawerContent = () => {
    const cust = this.state.selectedCustomer;
    if (!cust) return null;

    const { memoryData, loadingMemory, callSummaries, loadingCallSummaries } = this.state;
    const stageMap = {
      'GREETING': { name: 'Chào hỏi', color: '#8c8c8c' },
      'DISCOVERY': { name: 'Tìm hiểu', color: '#1890ff' },
      'PRESENTATION': { name: 'Tư vấn', color: '#722ed1' },
      'NEGOTIATION': { name: 'Thương lượng', color: '#fa8c16' },
      'CLOSING': { name: 'Chốt đơn', color: '#52c41a' },
      'HANDOVER': { name: 'Bàn giao', color: '#eb2f96' },
      'FOLLOW_UP': { name: 'Chăm sóc', color: '#13c2c2' }
    };

    return (
      <div className="customer-detail">
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <Avatar size={64} src={cust.avatar || '/images/default-avatar.png'} />
          <h3>{cust.full_name}</h3>
          <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 5 }}>
            {cust.phone_list && cust.phone_list.length > 0 ? cust.phone_list.map((phone, idx) => (
              <Tag key={idx} color="cyan">{phone}</Tag>
            )) : <span style={{color: '#999'}}>Chưa có SĐT</span>}
          </div>
          <div style={{ marginTop: 15 }}>
            <Button type="primary" icon={<MessageOutlined />} onClick={() => window.open(`/chat/${cust.contact_id}`, '_blank')}>
              Nhắn tin
            </Button>
          </div>
        </div>
        
        <h4>Thông tin cơ bản</h4>
        <ul style={{ listStyleType: 'none', paddingLeft: 0 }}>
          <li><strong>ID:</strong> {cust.contact_id}</li>
          <li><strong>Uid:</strong> {cust.user_id}</li>
          <li><strong>Kênh:</strong> {cust.channel_name} ({cust.type})</li>
          {cust.email_list && cust.email_list.length ? <li><strong>Email:</strong> {cust.email_list[0]}</li> : null}
          {cust.address ? <li><strong>Địa chỉ:</strong> {cust.address}</li> : null}
          {cust.source ? <li><strong>Nguồn:</strong> {cust.source}</li> : null}
          <li><strong>Cập nhật cuối:</strong> {moment(cust.updated_time || cust.last_time).format('DD/MM/YYYY HH:mm')}</li>
        </ul>

        <h4 style={{ marginTop: 20 }}>Thông tin AI (Lead Quality)</h4>
        <div style={{ background: '#f0f5ff', padding: '10px', borderRadius: '8px', border: '1px solid #adc6ff', marginBottom: 15 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '12px', color: '#666' }}>Giai đoạn AI</div>
              <div style={{ 
                fontWeight: 'bold', 
                color: stageMap[cust.ai_stage]?.color || '#1890ff',
                fontSize: '14px'
              }}>
                <Tag color={stageMap[cust.ai_stage]?.color || 'blue'}>
                  {stageMap[cust.ai_stage]?.name || cust.ai_stage || 'N/A'}
                </Tag>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '12px', color: '#666' }}>Điểm tiềm năng (Lead Score)</div>
              <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#f5222d' }}>
                {cust.lead_score || 0}/100
              </div>
            </div>
          </div>
          {cust.extracted_info?.notes && (
            <div style={{ marginTop: '10px', background: '#fff', padding: '8px', borderRadius: '6px', border: '1px dashed #adc6ff', fontSize: '13px' }}>
              <div style={{ fontSize: '11px', color: '#1890ff', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '2px' }}>Ghi chú từ AI</div>
              <div>{cust.extracted_info.notes}</div>
            </div>
          )}
        </div>

        {/* MEMORY SECTION */}
        <h4 style={{ marginTop: 10 }}>Bộ nhớ Trợ lý</h4>
        {loadingMemory ? (
          <div style={{ textAlign: 'center', padding: '10px' }}><Spin size="small" /> Đang tải...</div>
        ) : memoryData ? (
          <div style={{ background: '#f9f0ff', padding: '10px', borderRadius: '8px', border: '1px solid #d3adf7', marginBottom: 15 }}>
            {memoryData.memorySummary ? (
              <div style={{ marginBottom: '10px' }}>
                <div style={{ fontSize: '11px', color: '#722ed1', fontWeight: 'bold', textTransform: 'uppercase' }}>Tóm tắt hội thoại</div>
                <div style={{ fontSize: '13px', fontStyle: 'italic', color: '#555' }}>
                  "{memoryData.memorySummary}"
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', marginBottom: '10px', fontSize: '12px', color: '#999' }}>
                Chưa có tóm tắt
              </div>
            )}

            {memoryData.semanticMemory && (
              <div>
                <div style={{ fontSize: '11px', color: '#722ed1', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '5px' }}>Thông tin đã ghi nhớ</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {memoryData.semanticMemory.interests?.map((it, idx) => <Tag key={`int_${idx}`} color="purple">Quan tâm: {it}</Tag>)}
                  {memoryData.semanticMemory.budget && <Tag color="gold">Ngân sách: {memoryData.semanticMemory.budget}</Tag>}
                  {memoryData.semanticMemory.objections?.map((obj, idx) => <Tag key={`obj_${idx}`} color="volcano">Phản đối: {obj}</Tag>)}
                  {memoryData.semanticMemory.sentiment && <Tag color="blue">Thái độ: {memoryData.semanticMemory.sentiment}</Tag>}
                  {(memoryData.semanticMemory.customer?.phone || memoryData.semanticMemory.phone) && <Tag color="cyan">SĐT: {memoryData.semanticMemory.customer?.phone || memoryData.semanticMemory.phone}</Tag>}
                  {(memoryData.semanticMemory.customer?.email || memoryData.semanticMemory.email) && <Tag color="cyan">Email: {memoryData.semanticMemory.customer?.email || memoryData.semanticMemory.email}</Tag>}
                  {memoryData.semanticMemory.customer?.address && <Tag color="geekblue">Địa chỉ: {memoryData.semanticMemory.customer.address}</Tag>}
                  {memoryData.semanticMemory.appointment_date && <Tag color="green">Ngày hẹn: {memoryData.semanticMemory.appointment_date}</Tag>}
                  {memoryData.semanticMemory.branch_location && memoryData.semanticMemory.branch_location !== memoryData.semanticMemory.customer?.address && <Tag color="blue">Công ty: {memoryData.semanticMemory.branch_location}</Tag>}
                </div>
              </div>
            )}
            
            <div style={{ fontSize: '10px', color: '#999', marginTop: '10px', textAlign: 'right' }}>
              Cập nhật: {memoryData.memoryUpdatedAt ? moment(memoryData.memoryUpdatedAt).fromNow() : 'Chưa có'}
            </div>
          </div>
        ) : (
          <div style={{ fontSize: '12px', color: '#999', padding: '10px 0', marginBottom: 15 }}>Chưa có dữ liệu bộ nhớ.</div>
        )}

        <h4>Lịch sử tóm tắt cuộc gọi</h4>
        {loadingCallSummaries ? (
          <div style={{ textAlign: 'center', padding: '10px 0', marginBottom: 15 }}><Spin size="small" /> Đang tải...</div>
        ) : callSummaries.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 15 }}>
            {callSummaries.map((item) => (
              (() => {
                const structuredInfo = getStructuredCallSummaryTags(item.structured_data);
                return (
                  <div
                    key={item._id}
                    style={{
                      background: '#f6ffed',
                      border: '1px solid #b7eb8f',
                      borderRadius: 8,
                      padding: 10
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, color: '#666', marginBottom: 6 }}>
                      <span>{item.agent_name || 'Call center'}</span>
                      <span>{moment(item.called_at || item.created_time).format('DD/MM/YYYY HH:mm')}</span>
                    </div>
                    {structuredInfo.tags.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: item.summary_text || structuredInfo.notes ? 8 : 0 }}>
                        {structuredInfo.tags.map((tag, idx) => (
                          <Tag key={`${item._id || item.created_time}_structured_${idx}`} color={tag.color}>
                            {tag.label}: {tag.value}
                          </Tag>
                        ))}
                      </div>
                    ) : null}
                    {item.summary_text ? (
                      <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{item.summary_text}</div>
                    ) : null}
                    {structuredInfo.notes ? (
                      <div style={{ marginTop: 8, padding: 8, borderRadius: 6, background: '#fff', border: '1px dashed #b7eb8f', fontSize: 12, color: '#555' }}>
                        <div style={{ fontSize: 10, fontWeight: 'bold', textTransform: 'uppercase', color: '#52c41a', marginBottom: 2 }}>Ghi chú từ structured data</div>
                        <div>{structuredInfo.notes}</div>
                      </div>
                    ) : null}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, fontSize: 11, color: '#888', marginTop: 6 }}>
                      <span>AI: {item.sync_status}</span>
                    </div>
                  </div>
                );
              })()
            ))}
          </div>
        ) : (
          <div style={{ fontSize: '12px', color: '#999', padding: '10px 0', marginBottom: 15 }}>Chưa có lịch sử tóm tắt cuộc gọi.</div>
        )}

        <h4>Giai đoạn chăm sóc</h4>
        <div style={{ paddingBottom: 10 }}>
          {cust.tags && cust.tags.length > 0 ? cust.tags.map(t => (
            <Tag key={t.id} color={t.color || 'green'}>{t.name}</Tag>
          )) : <span style={{ fontSize: '12px', color: '#999' }}>Chưa gán nhãn</span>}
        </div>
        <ul style={{ listStyleType: 'none', paddingLeft: 0, marginTop: 10 }}>
          <li><strong>Sale phụ trách:</strong> {cust.assigned_sale || 'Chưa có'}</li>
        </ul>

      </div>
    );
  }

  render() {
    const columns = [
      {
        title: 'Khách hàng',
        dataIndex: 'full_name',
        key: 'full_name',
        render: (text, record) => (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Avatar src={record.avatar || '/images/default-avatar.png'} style={{ marginRight: 10 }} />
            <div>
              <div style={{ fontWeight: 600 }}>{text}</div>
              <div style={{ fontSize: 12, color: '#888' }}>
                {record.phone_list && record.phone_list.length > 0 ? record.phone_list[0] : 'Không có SĐT'}
              </div>
            </div>
          </div>
        )
      },
      {
        title: 'Kênh/Nguồn',
        dataIndex: 'channel_name',
        key: 'channel_name',
        render: (text, record) => {
          let icon = <GlobalOutlined style={{ color: '#1890ff' }} />;
          let typeLabel = record.type;
          
          if (record.type === 'direct' || record.type === 'comment') {
            icon = <FacebookOutlined style={{ color: '#1877F2' }} />;
            typeLabel = record.type === 'direct' ? 'Facebook (Inbox)' : 'Facebook (Comment)';
          } else if (record.type === 'zalo' || record.type === 'zalomessage') {
            icon = <WhatsAppOutlined style={{ color: '#0068ff' }} />;
            typeLabel = 'Zalo OA';
          } else if (record.type === 'telegram') {
            icon = <MessageOutlined style={{ color: '#0088cc' }} />;
            typeLabel = 'Telegram';
          } else if (record.type === 'webchat') {
            icon = <img src="../img/menu/new_icons/icon-zalo.png" alt="webchat" style={{ width: 14, height: 14 }}/>;
            typeLabel = 'Webchat';
          } else if (record.type === 'www') {
            typeLabel = 'Khách nhập dữ liệu';
          }

          return (
            <div>
              <div style={{ fontWeight: 500 }}>{text}</div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                {icon} <span style={{ marginLeft: 4 }}>{typeLabel}</span>
              </div>
            </div>
          );
        }
      },
      {
        title: 'Tiềm năng (AI)',
        dataIndex: 'lead_score',
        key: 'lead_score',
        render: (text, record) => (
          <div>
            <span style={{ fontWeight: 600, color: text > 70 ? 'green' : text > 40 ? 'orange' : 'red' }}>
              {text || 0} điểm
            </span>
            <br />
            <span style={{ fontSize: 12 }}>{record.ai_stage || 'GREETING'}</span>
          </div>
        )
      },
      {
        title: 'Giai đoạn (Tags)',
        dataIndex: 'tags',
        key: 'tags',
        render: (tags) => (
          <>
            {tags && tags.map(tag => (
              <Tag color={tag.color || 'blue'} key={tag.id}>{tag.name}</Tag>
            ))}
          </>
        )
      },
      {
        title: 'Sale phụ trách',
        dataIndex: 'assigned_sale',
        key: 'assigned_sale',
        render: (val, record) => val ? <span>{record.assigned_sale_name || val}</span> : <span style={{color: '#aaa'}}>Chưa có</span>
      },
      {
        title: 'Tương tác cuối',
        dataIndex: 'last_time',
        key: 'last_time',
        render: (val) => val ? moment(val).format('DD/MM/YYYY HH:mm') : ''
      },
      {
        title: 'Thao tác',
        key: 'action',
        render: (text, record) => (
          <Button type="primary" size="small" onClick={() => this.openDrawer(record)}>Chi tiết</Button>
        )
      }
    ];

    return (
      <div style={{ padding: 20, background: '#fff', minHeight: 'calc(100vh - 60px)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
          <div>
            <h2 style={{ marginBottom: 2 }}>Danh sách Khách hàng</h2>
            <span style={{ color: '#8c8c8c' }}>{this.state.total.toLocaleString('vi-VN')} khách hàng</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 8 }}>
            <Search
              placeholder="Tìm tên, SĐT..."
              allowClear
              enterButton="Tìm kiếm"
              size="middle"
              onSearch={this.onSearch}
              style={{ width: 300 }}
            />
            <Button icon={<UploadOutlined />} onClick={() => this.setState({ importModalVisible: true })}>
              Import Excel
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => this.setState({ createModalVisible: true })}>
              Thêm khách hàng
            </Button>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={this.state.data}
          rowKey="contact_id"
          loading={this.state.loading}
          scroll={{ x: 1100, y: 'calc(100vh - 280px)' }}
          pagination={{
            current: this.state.page,
            pageSize: this.state.limit,
            total: this.state.total
          }}
          onChange={this.handleTableChange}
        />

        <Drawer
          title="Chi tiết khách hàng"
          placement="right"
          width={400}
          onClose={this.closeDrawer}
          visible={this.state.drawerVisible}
        >
          {this.renderDrawerContent()}
        </Drawer>

        <CustomerCreateModal
          visible={this.state.createModalVisible}
          onCancel={() => this.setState({ createModalVisible: false })}
          onSuccess={this.handleCreateSuccess}
          fetchData={this.props.fetchData}
        />

        <CustomerImportModal
          visible={this.state.importModalVisible}
          onCancel={() => this.setState({ importModalVisible: false })}
          onSuccess={this.handleImportSuccess}
          fetchData={this.props.fetchData}
        />
      </div>
    );
  }
}
